import { useState, useCallback } from 'react';
import EntityActionRegistry from '../services/EntityActionRegistry';
import EntityActionHandler from '../services/EntityActionHandler';
import { debugLog } from '../utils/debugLog';

interface UseEntityActionsReturn {
    availableActions: any[];
    executeAction: (actionId: string, entityId?: string) => void;
    actionComponent: React.ReactElement | null;
    isActionAvailable: (actionId: string) => boolean;
    closeAction: () => void;
}

export const useEntityActions = (entityType: string): UseEntityActionsReturn => {
    const [actionComponent, setActionComponent] = useState<React.ReactElement | null>(null);

    const actionRegistry = EntityActionRegistry.getInstance();
    const actionHandler = EntityActionHandler.getInstance();

    const availableActions = actionRegistry.getActions(entityType);

    const executeAction = useCallback((actionId: string, entityId?: string) => {
        const component = actionHandler.executeAction({
            entityType,
            actionId,
            entityId,
            onClose: () => setActionComponent(null),
            onSuccess: (result) => {
                debugLog('Action completed successfully:', result);
                setActionComponent(null);
            }
        });

        if (component) {
            setActionComponent(component);
        }
    }, [entityType, actionHandler]);

    const isActionAvailable = useCallback((actionId: string) => {
        return actionHandler.isActionAvailable(entityType, actionId);
    }, [entityType, actionHandler]);

    const closeAction = useCallback(() => {
        setActionComponent(null);
    }, []);

    return {
        availableActions,
        executeAction,
        actionComponent,
        isActionAvailable,
        closeAction
    };
};
